<template>
  <div class="content">
    <div
        v-if="date && !daily"
        class="header-day"
    >
      <span>{{ weekday }}</span>
      <span>{{ dateString }}</span>
    </div>
    <div
      v-else-if="date && daily"
      class="header-day"
    >
      <span>{{ data.trainer }}</span>
    </div>
    <div class="columns">
      <div class="time-windows">
        <div
          v-for="(data, index) in cells.timeWindows"
          :key="index"
          class="column-day"
        >
          <cell :data="data"/>
        </div>
      </div>
      <div class="events">
        <div
          v-for="(data, index) in cells.events"
          :key="index"
          class="column-day"
        >
          <expandable-cell
            @refreshCalendar="$emit('refresh-calendar')"
            :data="data"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import {
  onMounted, watch, ref, computed, reactive,
} from 'vue';

import Cell from './Cell.vue';
import ExpandableCell from './ExpandableCell.vue';

dayjs.extend(require('dayjs/plugin/utc'));
dayjs.extend(require('dayjs/plugin/weekday'));

export default {
  name: 'Column',

  components: { Cell, ExpandableCell },

  props: {
    date: {
      type: Object,
      default: null,
    },
    startHour: Number, // full hour
    endHour: Number, // full hour
    step: Number, // in minutes
    data: Object,
    daily: Boolean,
  },

  setup(props) {
    const dateString = computed(() => props.date.format('DD-MM-YYYY'));
    const weekday = computed(() => props.date.locale('pl').format('dddd'));

    const preparedData = ref([]);

    function textColor(hexTripletColor) {
      const r = parseInt(hexTripletColor.substr(1, 2), 16);
      const g = parseInt(hexTripletColor.substr(3, 2), 16);
      const b = parseInt(hexTripletColor.substr(5, 2), 16);
      const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (brightness >= 128) ? 'black' : 'white';
    }

    const toEnd = ref((props.endHour - props.startHour) * 60);

    function emptyCell(lastPixels = 0, isOpacity = false) {
      let pixels = lastPixels;
      if (!pixels) pixels = (props.endHour - props.startHour) * props.step * (60 / props.step);
      toEnd.value -= pixels;
      return {
        pixels,
        color: '#FFFFFF',
        name: null,
        default_name: null,
        id: null,
        isOpacity,
        date: props.date,
        trainerId: props.data.id,
      };
    }

    function filledCell(lastPixels, activities) {
      toEnd.value -= lastPixels;
      const color = activities.color ? activities.color : '#AAAAAA';
      return {
        pixels: lastPixels,
        color,
        name: activities.name,
        default_name: activities.default_name,
        id: activities.id,
        start: activities.start_time,
        end: activities.end_time,
        fontColor: textColor(color),
        toEnd: toEnd.value,
        isEmpty: activities.isEmpty,
        date: props.date,
        trainerId: props.data.id,
        openable: true,
        isApproved: activities.isApproved,
        isPeriodic: activities.isPeriodic,
        smsSentCount: activities.smsSentCount || 0,
        smsConfirmedCount: activities.smsConfirmedCount || 0,
        clientsCount: activities.clientsCount || 0,
      };
    }

    function countLastPixels(startValue, endTime, startTime) {
      return startValue + ((endTime - startTime) / 3600000) * props.step * (60 / props.step);
    }

    const cells = reactive({
      timeWindows: [],
      events: [],
    });

    function createCells(field, isOpacity) {
      let arr = [];
      let lastHour = props.data.date.hour(props.startHour);
      if (!props.data[field] || !props.data[field].length) {
        arr.push(emptyCell(0, isOpacity));
      } else {
        let lastPixels = 0;
        // eslint-disable-next-line array-callback-return,consistent-return
        arr = props.data[field].map((activities) => {
          if (!activities.start_time.diff(lastHour)) {
            lastPixels = countLastPixels(0, activities.end_time, activities.start_time);
            lastHour = activities.end_time;
            return filledCell(lastPixels, activities);
          } if (activities.start_time.diff(lastHour) > 0) {
            const val = [];
            lastPixels = countLastPixels(0, activities.start_time, lastHour);
            val.push(emptyCell(lastPixels, true));
            lastHour = activities.start_time;
            lastPixels = countLastPixels(lastPixels, activities.end_time, activities.start_time);
            const pixels = countLastPixels(0, activities.end_time, activities.start_time);
            lastHour = activities.end_time;
            val.push(filledCell(pixels, activities));
            return val;
          }
        });
        if (toEnd.value > 0) {
          arr.push(emptyCell(toEnd.value, true));
        }
      }
      return arr;
    }

    watch(() => props.date, () => {
      toEnd.value = (props.endHour - props.startHour) * 60;
      cells.events = [];
      cells.timeWindows = [];
      if (props.data.date) {
        cells.events = createCells('events', true).flat();
        cells.timeWindows = createCells('time_windows').flat();
      }
    });

    onMounted(() => {
      if (props.data && props.data.date) {
        cells.events = createCells('events', true).flat();
        cells.timeWindows = createCells('time_windows').flat();
      }
    });

    return {
      cells,
      dateString,
      preparedData,
      weekday,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/colors.scss';

.content {
  display: flex;
  flex-direction: column;
}

.header-day {
  background-color: white;
  display: flex;
  flex-direction: column;
  height: 60px;
  justify-content: center;
  min-width: 110px;
  position: sticky;
  text-align: center;
  top: 0;
  width: calc((100vw - 75px) / 7);
  z-index: 25;
}

.column-day {
  width: calc((100vw - 75px) / 7);
}

.columns {
  position: relative;

  .events {
    pointer-events: auto;
    position: absolute;
    top: 0;
  }

  .time-windows {
    pointer-events: none;
  }
}

</style>
